/*
 * Portal de um equipamento: modo de acesso, aparencia e envio ao roteador, tudo
 * no mesmo lugar.
 *
 * A configuracao vale so para este MikroTik. Nao ha heranca de nenhum outro
 * lugar: campo vazio cai no padrao de fabrica, e mexer aqui nao altera os
 * outros equipamentos.
 */
import { useState } from 'react';
import { ApiRequestError, apiRequest } from '../lib/api';
import type { MikrotikDto, PortalProvisionDto } from '../lib/types';
import { AccessModeEditor } from '../components/AccessModeEditor';
import { ThemeEditor } from '../components/ThemeEditor';
import { Alert, Button, Card } from '../components/ui';

interface Props {
  mikrotik: MikrotikDto;
  podeEscrever: boolean;
  onClose: () => void;
}

export function PortalPanel({ mikrotik, podeEscrever, onClose }: Props) {
  const [enviando, setEnviando] = useState(false);
  /** Modo em vigor neste equipamento, informado pelo editor de acesso. */
  const [modo, setModo] = useState<string | null>(null);
  const [resultado, setResultado] = useState<PortalProvisionDto | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  async function enviarParaMikrotik() {
    setEnviando(true);
    setErro(null);
    setResultado(null);
    try {
      setResultado(
        await apiRequest<PortalProvisionDto>(
          `/admin/mikrotiks/${mikrotik.id}/provision-portal`,
          { method: 'POST' },
        ),
      );
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao enviar');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Card
      title={`Portal — ${mikrotik.name}`}
      action={
        <Button variant="ghost" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      <div className="space-y-6">
        {erro ? <Alert>{erro}</Alert> : null}

        {/* Como o cliente entra, antes de como a tela se parece. */}
        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Modo de acesso</h3>
          <AccessModeEditor
            mikrotikId={mikrotik.id}
            podeEscrever={podeEscrever}
            onModo={setModo}
          />
        </div>

        <ThemeEditor
          mikrotikId={mikrotik.id}
          podeEscrever={podeEscrever}
          /* Vem do editor acima, ja com o rascunho: trocar o modo muda a previa
             na hora do clique, sem exigir salvar para so entao conferir. */
          {...(modo === null ? {} : { modo })}
          acoesExtras={
            <Button onClick={() => void enviarParaMikrotik()} disabled={enviando}>
              {enviando ? 'Enviando...' : 'Enviar para o MikroTik'}
            </Button>
          }
        />

        {/* O que o envio faz, e o resultado de cada passo */}
        <div className="border-t border-slate-200 pt-4">
          <p className="text-sm font-medium text-slate-700">O que "Enviar para o MikroTik" faz</p>
          <ul className="mt-1 list-inside list-disc text-xs text-slate-600">
            <li>Libera o domínio do portal no walled garden (HTTP)</li>
            <li>Libera o IP do servidor na porta 443 (HTTPS)</li>
            <li>Instala a página de login no roteador</li>
          </ul>
          <p className="mt-2 text-xs text-slate-500">
            A aparência é lida pelo portal a cada acesso — depois do primeiro envio, mudanças de
            cor, logo e texto valem na hora, sem precisar reenviar.
          </p>

          {resultado ? (
            <div className="mt-3 space-y-2">
              {resultado.ok ? (
                <Alert tone="success">
                  Portal instalado. Conecte um dispositivo na rede de visitantes para conferir.
                </Alert>
              ) : (
                <Alert>Algum passo falhou. Veja o detalhe abaixo.</Alert>
              )}

              <ol className="space-y-1.5 text-sm">
                {resultado.steps.map((passo) => (
                  <li key={passo.id} className="flex gap-2">
                    <span
                      className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${
                        passo.status === 'done'
                          ? 'bg-emerald-100 text-emerald-800'
                          : passo.status === 'skipped'
                            ? 'bg-slate-100 text-slate-600'
                            : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {passo.status === 'done'
                        ? 'feito'
                        : passo.status === 'skipped'
                          ? 'já existia'
                          : 'falhou'}
                    </span>
                    <span className="text-slate-700">
                      {passo.title}
                      {passo.status === 'failed' ? (
                        <span className="block text-xs text-red-700">{passo.detail}</span>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ol>

              <p className="text-xs text-slate-500">
                Endereço do portal:{' '}
                <code className="font-mono break-all">{resultado.portalUrl}</code>
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
